import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { expenseKeys, accountKeys, transactionKeys } from '@/lib/query-keys';
import { expenseService } from '@/services/expense.service';
import { useExpenseFilterStore } from '@/stores/expenseFilterStore';

export function useExpenses() {
  const queryClient = useQueryClient();
  const filters = useExpenseFilterStore();

  // datePreset drives the toolbar dropdown only; strip it before any request
  // (the API rejects unknown query params). Its computed dateFrom/dateTo go over.
  const toApiParams = (f: typeof filters.current) => {
    const rest: Record<string, unknown> = { ...f };
    delete rest.datePreset;
    return rest;
  };

  const expensesQuery = useQuery({
    queryKey: expenseKeys.list(filters.current),
    queryFn: () => expenseService.getAll(toApiParams(filters.current)),
    placeholderData: keepPreviousData,
  });

  const createExpense = useMutation({
    mutationFn: (data: Parameters<typeof expenseService.create>[0]) => expenseService.create(data),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: expenseKeys.lists() }); queryClient.invalidateQueries({ queryKey: accountKeys.all }); queryClient.invalidateQueries({ queryKey: transactionKeys.all }); toast.success('Expense created'); },
    onError: () => toast.error('Failed to create expense'),
  });

  const updateExpense = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof expenseService.update>[1] }) => expenseService.update(id, data),
    onSuccess: (_, { id }) => { queryClient.invalidateQueries({ queryKey: expenseKeys.lists() }); queryClient.invalidateQueries({ queryKey: expenseKeys.detail(id) }); toast.success('Expense updated'); },
    onError: () => toast.error('Failed to update expense'),
  });

  const deleteExpense = useMutation({
    mutationFn: (id: string) => expenseService.delete(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: expenseKeys.lists() }); toast.success('Expense deleted'); },
    onError: () => toast.error('Failed to delete expense'),
  });

  const submitExpense = useMutation({
    mutationFn: (id: string) => expenseService.submit(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: expenseKeys.all }); toast.success('Expense submitted for approval'); },
    onError: () => toast.error('Failed to submit expense'),
  });

  const uploadAttachment = useMutation({
    mutationFn: ({ id, file }: { id: string; file: File }) => expenseService.uploadAttachment(id, file),
    onSuccess: (_, { id }) => { queryClient.invalidateQueries({ queryKey: expenseKeys.detail(id) }); toast.success('Attachment uploaded'); },
    onError: () => toast.error('Failed to upload attachment'),
  });

  const deleteAttachment = useMutation({
    mutationFn: ({ id, attachmentId }: { id: string; attachmentId: string }) => expenseService.deleteAttachment(id, attachmentId),
    onSuccess: (_, { id }) => { queryClient.invalidateQueries({ queryKey: expenseKeys.detail(id) }); toast.success('Attachment deleted'); },
    onError: () => toast.error('Failed to delete attachment'),
  });

  return {
    expenses: expensesQuery.data?.data ?? [],
    meta: expensesQuery.data?.meta,
    isLoading: expensesQuery.isLoading,
    filters,
    createExpense, updateExpense, deleteExpense, submitExpense, uploadAttachment, deleteAttachment,
    exportData: (format: string, override?: Record<string, unknown>) =>
      expenseService.exportData(format, override ?? toApiParams(filters.current)),
  };
}

export function useExpense(id: string) {
  const expenseQuery = useQuery({ queryKey: expenseKeys.detail(id), queryFn: () => expenseService.getById(id), enabled: !!id });
  return { expense: expenseQuery.data?.data, isLoading: expenseQuery.isLoading };
}
